import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from "classnames";
import { Status, useSelector } from "@jti/store";
import { DEPRECATED_Typography as Typography, Loading, replaceStringPlaceholder } from "@jti/ui";
import trackCtaClick from "../../helpers/functions/trackCtaClick";
import useLoggedUser from "../../helpers/hooks/useLoggedUser";
import { AccountActionButtons } from "../account-action-buttons/AccountActionButtons";
import { Button } from "../button/Button";
import { ConditionalView } from "../conditional-view/ConditionalView";
import { ImageBackgroundContainer } from "../image-background-container/ImageBackgroundContainer";
import styles from "./CallToAction.module.scss";
const getTitleToShow = ({ isLogged, isRestrictedContent, title, restrictedTitle, titleLoggedIn, }) => {
    if (isLogged && isRestrictedContent)
        return restrictedTitle;
    return isLogged && titleLoggedIn ? titleLoggedIn : title;
};
const getConfigVariant = (isLogged, isRestrictedContent, isRewards) => {
    if (isLogged && isRewards)
        return isRestrictedContent ? "restricted" : "rewards";
    return "default";
};
export const CallToAction = ({ variant = "cta", buttonImage, theme = "light", headerPosition = "left", buttonPosition = "left", ctaTitle, ctaUrl, secondaryCtaTitle, secondaryCtaUrl, ctaTitleLoggedIn, ctaUrlLoggedIn, secondaryCtaTitleLoggedIn, secondaryCtaUrlLoggedIn, title, text, description, background, mobileBackground, titleLoggedIn, animatedBackground, withGradient, animatedButton, animatedLabel, restrictedContent, restrictedTitle, restrictedDescription, ctaRestrictedTitle, ctaRestrictedUrl, secondaryCtaRestrictedTitle, secondaryCtaRestrictedUrl, ctaHeightAuto = false, }) => {
    const [isLogged] = useLoggedUser();
    const { points: { balance, status }, } = useSelector((state) => state.loyaltyCore);
    const isRestricted = useSelector((state) => state.loyaltyCore.isRestricted);
    const showRestrictedContent = restrictedContent && isRestricted;
    const isRewardsAction = variant === "rewards";
    const isCallToAction = variant === "cta";
    const isWhatIsPloom = variant === "whatisploom";
    const titleToShow = getTitleToShow({
        title,
        restrictedTitle,
        isLogged,
        isRestrictedContent: !!showRestrictedContent,
        titleLoggedIn,
    });
    const shouldLazyLoadTitle = titleToShow.includes("userPoints") && status !== Status.COMPLETED && status !== Status.FAILED;
    const personalizedTitle = replaceStringPlaceholder(titleToShow, [{ placeholder: "userPoints", value: balance }], false);
    const textToShow = showRestrictedContent && isLogged ? restrictedDescription : text;
    const themeColor = theme === "dark" ? "black" : "white";
    const classes = classNames(styles.cta, {
        [styles.ctaDefault]: variant === "cta",
        [styles.ctaWhatIsPloom]: variant === "whatisploom",
        [styles.ctaRewards]: variant === "rewards",
        [styles.ctaLight]: theme === "light",
        [styles.ctaDark]: theme === "dark",
        [styles.ctaHeightAuto]: ctaHeightAuto && variant === "cta",
    });
    const ctaSectionClass = classNames(styles.ctaSection, {
        [styles.ctaRight]: headerPosition === "right",
    });
    const ctaSecondarySectionClass = classNames(styles.ctaSecondarySection, {
        [styles.ctaRight]: buttonPosition === "right" && !description,
    });
    const buttonsConfig = {
        restricted: {
            signInUrl: ctaRestrictedUrl,
            signInTitle: ctaRestrictedTitle,
            registerUrl: secondaryCtaRestrictedUrl,
            registerTitle: secondaryCtaRestrictedTitle,
            trackingEvent: ctaRestrictedTitle,
        },
        rewards: {
            signInUrl: ctaUrlLoggedIn,
            signInTitle: ctaTitleLoggedIn,
            registerUrl: secondaryCtaUrlLoggedIn,
            registerTitle: secondaryCtaTitleLoggedIn,
            trackingEvent: ctaTitleLoggedIn,
        },
        default: {
            signInUrl: ctaUrl,
            signInTitle: ctaTitle,
            registerUrl: secondaryCtaUrl,
            registerTitle: secondaryCtaTitle,
        },
    };
    const configVariant = getConfigVariant(isLogged, !!showRestrictedContent, isRewardsAction);
    const showGradient = isRewardsAction || (isWhatIsPloom && withGradient);
    if (isLogged && isCallToAction)
        return null;
    return (_jsx(ImageBackgroundContainer, { background: background, mobileBackground: mobileBackground, withAnimation: animatedBackground, children: _jsxs("div", { className: classNames({
                [styles.ctaAnimatedButton]: animatedButton,
            }), children: [showGradient && _jsx("div", { className: styles.ctaGradient, "data-testid": "call-to-action-gradient" }), _jsxs("section", { className: classes, "data-testid": "cta", children: [_jsxs("div", { className: styles.ctaContainer, children: [_jsxs("section", { "data-testid": "call-to-action-section-cta", className: ctaSectionClass, children: [shouldLazyLoadTitle ? (_jsx(Loading, { whiteDots: true })) : (_jsx(Typography, { type: !isCallToAction ? "xlarge-title" : "h1", color: themeColor, weight: "light", dataTestid: "cta-join-title", className: styles.ctaTitle, children: personalizedTitle })), _jsx(Typography, { type: "text", color: themeColor, weight: "light", dataTestid: "cta-join-description", children: textToShow })] }), _jsx(ConditionalView, { condition: isWhatIsPloom, children: _jsxs("section", { "data-testid": "call-to-action-section-whatisploom", className: ctaSecondarySectionClass, children: [description && (_jsx(Typography, { className: styles.ctaSecondarySectionDescription, type: "text", color: themeColor, weight: "light", dataTestid: "whatisploom-description", children: description })), _jsxs("div", { className: styles.ctaAnimationWrapper, children: [animatedButton && animatedLabel && (_jsx(Typography, { type: "text", color: "white", weight: "bold", className: styles.ctaAnimatedLabel, children: animatedLabel })), buttonImage ? (_jsxs("a", { href: ctaUrl, "data-testid": "cta-image-button-link", className: styles.ctaImageButton, children: [_jsx("img", { src: buttonImage, alt: "link-image" }), _jsx(Typography, { className: styles.ctaImageButtonText, type: "table-title", color: themeColor, children: ctaTitle })] })) : (_jsx(Button, { weight: "bold", buttonType: themeColor, url: ctaUrl, className: styles.ctaButton, dataTestId: "cta-register-button", onClick: () => trackCtaClick(ctaTitle, "LoyaltyCallToAction"), children: ctaTitle }))] })] }) })] }), _jsx(ConditionalView, { condition: !isWhatIsPloom, children: _jsx(AccountActionButtons, { ...buttonsConfig[configVariant], buttonType: isRewardsAction ? ["white", "transparent"] : undefined, className: styles.ctaAccountButtons }) })] })] }) }));
};
//# sourceMappingURL=CallToAction.js.map